import { lazy, Suspense, useState, useCallback } from "react";

// Loaded the first time a record is opened. It brings every details body with
// it (orders, requests, products...), and the layout mounts this hook on every
// page, so a static import put all of that in the entry bundle.
const RelatedOperationModal = lazy(() => import("../components/relatedOperation/RelatedOperationModal"));

/**
 * Shared "open a Related To operation in a popup, in place" behaviour used
 * by the wallet ledger, the dashboard, the history page, the notifications
 * page, and the notifications bell dropdown.
 *
 * Usage:
 *   const { openRelated, relatedModal } = useRelatedOperationModal();
 *   <button onClick={() => openRelated(toRelatedTarget(entry))}>...</button>
 *   {relatedModal}
 *
 * `target` shape: { entityType, entityId, fallbackLabel, entry }.
 * See toRelatedTarget() in utils/notificationsDisplay.js and
 * buildTxRelatedTarget() in utils/walletTxDisplay.js.
 */
export function useRelatedOperationModal() {
  const [target, setTarget] = useState(null);

  const openRelated = useCallback((next) => {
    if (next?.entityType) setTarget(next);
  }, []);

  const closeRelated = useCallback(() => setTarget(null), []);

  const relatedModal = target ? (
    <Suspense fallback={null}>
    <RelatedOperationModal
      key={`${target.entityType}:${target.entityId ?? "-"}`}
      entityType={target.entityType}
      entityId={target.entityId}
      fallbackLabel={target.fallbackLabel}
      entry={target.entry}
      onClose={closeRelated}
    />
    </Suspense>
  ) : null;

  return { openRelated, closeRelated, relatedModal };
}
